import { useCallback, useRef, type RefObject } from 'react';
import { usePlayerStore } from '@/store/usePlayerStore';

interface ProgressBarProps {
  videoRef: RefObject<HTMLVideoElement | null>;
  visible: boolean;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds)) return '--:--';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function ProgressBar({ videoRef, visible }: ProgressBarProps) {
  const currentTime = usePlayerStore((s) => s.currentTime);
  const duration = usePlayerStore((s) => s.duration);
  const bufferedEnd = usePlayerStore((s) => s.bufferedEnd);
  const isLive = usePlayerStore((s) => s.isLive);

  const barRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const seekFraction = useCallback(
    (clientX: number) => {
      const bar = barRef.current;
      const video = videoRef.current;
      if (!bar || !video || !duration || !isFinite(duration)) return;
      const rect = bar.getBoundingClientRect();
      const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      video.currentTime = fraction * duration;
    },
    [videoRef, duration]
  );

  const handleClick = (e: React.MouseEvent) => {
    seekFraction(e.clientX);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    dragging.current = true;
    seekFraction(e.clientX);
    const handleMove = (ev: MouseEvent) => seekFraction(ev.clientX);
    const handleUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const buffered = duration > 0 ? (bufferedEnd / duration) * 100 : 0;

  return (
    <div
      className={`transition-opacity duration-200 ${
        visible || dragging.current ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      <div
        ref={barRef}
        className="relative h-1 bg-white/20 cursor-pointer group/progress mx-3"
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        role="slider"
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={currentTime}
        tabIndex={0}
      >
        <div
          className="absolute inset-y-0 left-0 bg-white/40 rounded-full transition-none"
          style={{ width: `${buffered}%` }}
        />
        <div
          className="absolute inset-y-0 left-0 bg-accent rounded-full transition-none"
          style={{ width: `${progress}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-accent rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity shadow"
          style={{ left: `calc(${progress}% - 6px)` }}
        />
      </div>

      <div className="flex items-center justify-between px-3 mt-0.5 text-xs text-white/60">
        <span>{formatTime(currentTime)}</span>
        <span>{isLive ? '\u25CF LIVE' : formatTime(duration)}</span>
      </div>
    </div>
  );
}
